import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthProvider'
import type { OrganisationRoleCode } from '../lib/governance-api'
import {
  loadOrganisationAccessContexts,
  type CustomerModuleCode,
  type OrganisationAccessContext,
} from './service-access-api'

type TenantContextValue = {
  organisations: OrganisationAccessContext[]
  current: OrganisationAccessContext | null
  loading: boolean
  error: string | null
  setCurrentOrganisationId: (organisationId: string) => void
  hasRole: (role: OrganisationRoleCode) => boolean
  hasModule: (module: CustomerModuleCode) => boolean
  servicePermitted: boolean
  refreshOrganisations: () => Promise<void>
}

const TenantContext = createContext<TenantContextValue | null>(null)
const storageKey = 'swell.currentOrganisationId'

export function TenantProvider({ children }: PropsWithChildren) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [currentOrganisationId, setCurrentOrganisationIdState] = useState<string | null>(() => {
    const requested = new URLSearchParams(window.location.search).get('supportOrganisation')
    return requested || window.localStorage.getItem(storageKey)
  })

  const organisationQuery = useQuery({
    queryKey: ['organisation-access-contexts', user?.id],
    queryFn: loadOrganisationAccessContexts,
    enabled: Boolean(user),
    retry: 1,
  })

  const organisations = useMemo(
    () => organisationQuery.data ?? [],
    [organisationQuery.data],
  )

  useEffect(() => {
    if (!user) {
      setCurrentOrganisationIdState(null)
      return
    }

    // Do not interpret the query's initial empty data shape as "no organisations".
    // In particular, preserve a deliberately selected suspended organisation until
    // the membership-only access-context query has completed so the customer sees
    // its restriction message and can switch to another available organisation.
    if (!organisationQuery.isSuccess) return

    if (organisations.length === 0) {
      setCurrentOrganisationIdState(null)
      window.localStorage.removeItem(storageKey)
      return
    }

    const selectedStillAllowed = organisations.some(
      (access) => access.organisation.id === currentOrganisationId,
    )

    if (selectedStillAllowed) {
      const url = new URL(window.location.href)
      if (url.searchParams.has('supportOrganisation')) {
        url.searchParams.delete('supportOrganisation')
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
      }
      window.localStorage.setItem(storageKey, currentOrganisationId as string)
      return
    }

    if (!selectedStillAllowed) {
      const fallback = organisations.find((access) => access.service.permitted) ?? organisations[0]

      if (!fallback) return

      setCurrentOrganisationIdState(fallback.organisation.id)
      window.localStorage.setItem(storageKey, fallback.organisation.id)
    }
  }, [currentOrganisationId, organisationQuery.isSuccess, organisations, user])

  const current = organisations.find(
    (access) => access.organisation.id === currentOrganisationId,
  ) ?? null

  // A successful access-context query and a resolved current organisation are one
  // loading boundary. Without this, a deep link can render for one frame with
  // current === null before the fallback selection effect has run, causing pages
  // to misinterpret an existing tenant member as a user with no organisation.
  const resolvingCurrentOrganisation = Boolean(user)
    && !organisationQuery.isLoading
    && organisations.length > 0
    && !current

  const setCurrentOrganisationId = useCallback(
    (organisationId: string) => {
      // Selection is deliberately restricted to contexts returned by the trusted
      // membership-only API. RLS/RPC checks remain authoritative after selection.
      if (!organisations.some((access) => access.organisation.id === organisationId)) return

      setCurrentOrganisationIdState(organisationId)
      window.localStorage.setItem(storageKey, organisationId)
    },
    [organisations],
  )

  const refreshOrganisations = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['organisation-access-contexts', user?.id] })
  }, [queryClient, user?.id])

  const hasRole = useCallback(
    (role: OrganisationRoleCode) => Boolean(current?.roles.includes(role)),
    [current],
  )

  const hasModule = useCallback(
    (module: CustomerModuleCode) => Boolean(current?.service.permitted && current.entitledModules.includes(module)),
    [current],
  )

  const value = useMemo<TenantContextValue>(
    () => ({
      organisations,
      current,
      loading: Boolean(user) && (organisationQuery.isLoading || resolvingCurrentOrganisation),
      error: organisationQuery.error instanceof Error ? organisationQuery.error.message : null,
      setCurrentOrganisationId,
      hasRole,
      hasModule,
      servicePermitted: Boolean(current?.service.permitted),
      refreshOrganisations,
    }),
    [
      current,
      hasModule,
      hasRole,
      organisationQuery.error,
      organisationQuery.isLoading,
      organisations,
      refreshOrganisations,
      resolvingCurrentOrganisation,
      setCurrentOrganisationId,
      user,
    ],
  )

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
}

export function useTenant() {
  const context = useContext(TenantContext)

  if (!context) throw new Error('useTenant must be used within TenantProvider')
  return context
}
